<script>
import { getTemplateGroupedList, getTemplateItem } from "@/entities/template";
import { getDirectoryItem } from "@/entities/directory";
import {
  getExecInscriptionTypes,
  createApplicationFromTemplate,
} from "@/entities/application";
import { createStepRun } from "@/entities/transaction";
import { toast } from "@/shared/store/alertStore";
import { FormSheet } from "@/shared/ui/form-sheet";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";
import { CATEGORY_CODES } from "@/shared/lib/constants";
import { Search, ArrowLeft, SlidersHorizontal } from "lucide-vue-next";

export default {
  name: "CreateInscription",
  components: {
    Button,
    FormSheet,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Search,
    ArrowLeft,
    SlidersHorizontal,
    Spinner,
  },

  props: {
    title: {
      type: String,
      default: "",
    },
    open: {
      type: Boolean,
      default: false,
    },
    predefinedCategories: {
      type: Array,
      default: () => [CATEGORY_CODES.APPLICATIONS],
    },
    routeName: {
      type: String,
      default: "client_application_edit",
    },
  },

  emits: ["update:open", "close"],

  data() {
    return {
      visible: false,

      typesLoading: false,
      types: [],
      selectedType: null,

      initialLoading: false,
      template_categories: [],
      categories: [],

      form: {
        reference_code: null,
        template_name: null,
      },

      _searchTimer: null,
      submitting: false,
      submittingCode: null,

      typeSearch: "",
      showFilters: false,
    };
  },

  computed: {
    locale() {
      return this.$i18n.locale;
    },
    typeOptions() {
      return this.types.map((item) => {
        const labels = item.value || {};
        const label =
          labels[this.locale] ||
          labels.ru ||
          labels.kz ||
          labels.en ||
          item.code;
        return { code: item.code, label };
      });
    },
    filteredTypeOptions() {
      const q = (this.typeSearch || "").trim().toLowerCase();
      if (!q) return this.typeOptions;
      return this.typeOptions.filter(
        (opt) =>
          opt.label.toLowerCase().includes(q) ||
          opt.code.toLowerCase().includes(q)
      );
    },
    selectedTypeLabel() {
      const found = this.typeOptions.find((t) => t.code === this.selectedType);
      return found ? found.label : "";
    },
    drawerWidth() {
      return this.selectedType ? 1080 : 600;
    },
    filteredCategories() {
      if (this.predefinedCategories.length) {
        return this.template_categories.filter((cat) =>
          this.predefinedCategories.includes(cat.code)
        );
      }
      return this.template_categories;
    },
  },

  watch: {
    open(val) {
      this.visible = val;
      if (val) {
        this.resetState();
        this.loadTypes();
      }
    },
    visible(val) {
      this.$emit("update:open", val);
      if (!val) this.$emit("close");
    },
    "form.reference_code": "debouncedLoad",
    "form.template_name": "debouncedLoad",
  },

  async created() {
    if (this.open) {
      this.visible = true;
      await this.loadTypes();
    }
  },

  beforeUnmount() {
    clearTimeout(this._searchTimer);
  },

  methods: {
    resetState() {
      this.selectedType = null;
      this.form.reference_code = null;
      this.form.template_name = null;
      this.template_categories = [];
    },

    async loadTypes() {
      this.typesLoading = true;
      try {
        const data = await getExecInscriptionTypes();
        if (data?.success && Array.isArray(data.payload?.items)) {
          this.types = data.payload.items;
        } else {
          this.types = [];
        }
      } catch (e) {
        console.error("Ошибка загрузки видов исполнительной надписи:", e);
        this.types = [];
      } finally {
        this.typesLoading = false;
      }
    },

    async selectType(code) {
      this.selectedType = code;
      this.form.reference_code = null;
      this.form.template_name = null;
      await Promise.all([
        this.getTemplateCategories(),
        this.loadGroupedTemplates(),
      ]);
    },

    backToTypes() {
      this.selectedType = null;
      this.form.reference_code = null;
      this.form.template_name = null;
      this.template_categories = [];
    },

    async getTemplateCategories() {
      if (this.categories.length) return;
      try {
        const data = await getDirectoryItem("notarial_action_category");
        if (data?.success && data.payload?.children) {
          this.categories = data.payload.children;
        }
      } catch (error) {
        console.error("Ошибка загрузки категорий:", error);
      }
    },

    debouncedLoad() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.loadGroupedTemplates();
      }, 500);
    },

    async loadGroupedTemplates() {
      this.initialLoading = true;

      try {
        const params = {};

        if (this.predefinedCategories.length) {
          params.category_codes = this.predefinedCategories.join(",");
        }

        if (this.form.reference_code) {
          params.category_codes = params.category_codes
            ? params.category_codes + "," + this.form.reference_code
            : this.form.reference_code;
        }

        if (this.form.template_name) {
          params.q = this.form.template_name;
        }

        const res = await getTemplateGroupedList(params);

        if (!res?.success || !Array.isArray(res.payload)) {
          this.template_categories = [];
          return;
        }

        this.template_categories = res.payload
          .map((cat) => ({
            code: cat.code,
            name: cat.name || cat.name_kz || cat.code,
            templates: (cat.items || []).map((item) => ({
              code: item.code,
              name: item.name || item.name_kz || item.code,
            })),
          }))
          .filter((cat) => cat.templates.length > 0);
      } catch (err) {
        console.error("Ошибка загрузки шаблонов:", err);
        this.template_categories = [];
      } finally {
        this.initialLoading = false;
      }
    },

    async createInscription(code) {
      if (this.submitting) return;
      this.submitting = true;
      this.submittingCode = code;
      try {
        const tplRes = await getTemplateItem(code);
        if (!tplRes?.success || !tplRes.payload) {
          toast.error(this.$t("Не удалось загрузить шаблон"));
          return;
        }
        const tpl = tplRes.payload;

        const payload = {
          content: tpl.content,
          content_kz: tpl.content_kz,
          content_en: tpl.content_en,
          languages: tpl.languages,
          fields: tpl.fields,
          template_fields: tpl.template_fields,
          notary_action_code: tpl.code,
        };
        if (this.selectedType) {
          payload.exec_inscription_type = this.selectedType;
        }
        const category = this.predefinedCategories?.[0] || null;
        if (category) {
          payload.category_code = category;
        }

        const data = await createApplicationFromTemplate(tpl.code, payload);
        if (!data?.success || !data.payload?.id) {
          toast.error(this.$t("Не удалось создать заявление"));
          return;
        }

        await this.doCreateStepRun(data.payload, tpl);
        this.handleClose();
        this.$router.push({
          name: this.routeName,
          params: { id: data.payload.id },
          query: { welcome: "1" },
        });
      } finally {
        this.submitting = false;
        this.submittingCode = null;
      }
    },

    async doCreateStepRun(application, tpl) {
      const params = {
        notary_transaction_id: application.id,
        notary_transaction_uuid: application.uuid,
        template_uuid: tpl?.uuid,
      };
      const response = await createStepRun(params);
      if (!response?.success) {
        console.warn("[CreateInscription] createStepRun failed", response);
      }
    },

    handleClose() {
      this.visible = false;
      this.$emit("close");
    },
    onCategoryChange(val) {
      this.form.reference_code = val === "__clear__" ? null : val;
    },
  },
};
</script>

<template>
  <FormSheet
    v-model:open="visible"
    :title="title || $t('Создать заявление')"
    :width="drawerWidth"
    :show-footer="false"
    @close="handleClose"
  >
    <div class="ci-layout">
      <!-- ЛЕВЫЙ блок: список видов исполнительной надписи -->
      <div class="ci-pane ci-types-pane">
        <div class="relative">
          <Search
            class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
          />
          <Input
            v-model="typeSearch"
            :placeholder="$t('Поиск по названию')"
            class="pl-9"
          />
        </div>

        <template v-if="typesLoading">
          <ul class="ci-card-list">
            <li
              v-for="n in 5"
              :key="n"
              class="ci-card-item ci-card-item--skeleton"
            >
              <div class="h-4 bg-muted rounded-md w-2/3 animate-pulse" />
            </li>
          </ul>
        </template>

        <template v-else>
          <div
            v-if="!filteredTypeOptions.length"
            class="text-center text-muted-foreground py-6 text-sm"
          >
            {{ $t("Ничего не найдено") }}
          </div>

          <ul v-else class="ci-card-list">
            <li
              v-for="opt in filteredTypeOptions"
              :key="opt.code"
              class="ci-card-item"
              :class="{ 'is-active': selectedType === opt.code }"
              @click="selectType(opt.code)"
            >
              <div class="flex-1 min-w-0">
                <div class="text-xs text-muted-foreground mb-0.5">
                  {{ opt.code }}
                </div>
                <div
                  class="text-foreground text-sm font-medium leading-5 line-clamp-4"
                >
                  {{ opt.label }}
                </div>
              </div>
              <svg-icon
                name="huge-chevron-right"
                class="w-4 h-4 flex-shrink-0 text-muted-foreground"
              />
            </li>
          </ul>
        </template>
      </div>

      <!-- ПРАВЫЙ блок: список шаблонов (виден после выбора типа) -->
      <transition name="ci-slide">
        <div v-if="selectedType" class="ci-pane ci-templates-pane">
          <div class="ci-filters">
            <div class="ci-filters-row">
              <Button
                variant="outline"
                @click="backToTypes"
                :title="$t('Назад')"
                class="h-9 w-9"
              >
                <ArrowLeft :size="14" />
              </Button>
              <div class="relative flex-1">
                <Search
                  class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                />
                <Input
                  v-model="form.template_name"
                  :placeholder="$t('Поиск по названию шаблона')"
                  class="pl-9"
                />
              </div>

              <Button
                variant="outline"
                :title="$t('Фильтры')"
                @click="showFilters = !showFilters"
                class="h-9 w-9"
              >
                <SlidersHorizontal :size="16" />
              </Button>
            </div>

            <Select
              v-if="showFilters"
              :model-value="form.reference_code || undefined"
              @update:model-value="onCategoryChange"
            >
              <SelectTrigger class="w-full" @clear="onCategoryChange(null)">
                <SelectValue :placeholder="$t('Выберите категорию')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__clear__">
                  {{ $t("Все категории") }}
                </SelectItem>
                <SelectItem
                  v-for="item in categories"
                  :key="item.code"
                  :value="String(item.code)"
                >
                  {{ item.name }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <main class="ci-templates-body">
            <template v-if="initialLoading">
              <div v-for="n in 4" :key="n" class="group">
                <div class="h-4 bg-muted rounded-md w-1/3 mb-3 animate-pulse" />
                <ul>
                  <li v-for="m in 3" :key="m" class="animate-pulse">
                    <div class="h-4 bg-muted rounded-md w-2/3" />
                  </li>
                </ul>
              </div>
            </template>

            <template v-else>
              <div
                v-if="!filteredCategories.length"
                class="text-center text-muted-foreground py-10"
              >
                {{ $t("Ничего не найдено") }}
              </div>

              <div
                v-for="cat in filteredCategories"
                :key="cat.code"
                class="group"
              >
                <!--                <div-->
                <!--                  class="text-muted-foreground text-sm font-medium leading-5"-->
                <!--                >-->
                <!--                  {{ cat.name }}-->
                <!--                </div>-->

                <ul v-if="cat.templates.length">
                  <li v-for="action in cat.templates" :key="action.code">
                    <div
                      class="w-full flex justify-between items-center cursor-pointer"
                      :class="{
                        'pointer-events-none opacity-60':
                          submitting && submittingCode !== action.code,
                      }"
                      @click="createInscription(action.code)"
                    >
                      <div
                        class="flex-1 text-foreground text-sm font-medium leading-5 line-clamp-2"
                      >
                        {{ action.name }}
                      </div>
                      <Spinner
                        v-if="submittingCode === action.code"
                        size="sm"
                        class="flex-shrink-0"
                      />
                      <svg-icon
                        v-else
                        name="huge-chevron-right"
                        class="w-4 h-4 flex-shrink-0"
                      />
                    </div>
                  </li>
                </ul>

                <div v-else class="text-muted-foreground text-sm px-1">
                  {{ $t("Нет шаблонов") }}
                </div>
              </div>
            </template>
          </main>
        </div>
      </transition>
    </div>
  </FormSheet>
</template>

<style scoped lang="scss">
.ci-layout {
  @apply w-full h-full flex gap-4 overflow-hidden;
}

.ci-pane {
  @apply flex flex-col min-h-0;
}

.ci-types-pane {
  @apply w-[550px] flex-shrink-0 gap-6;
}

.ci-templates-pane {
  @apply flex-1 min-w-0 border-l border-solid border-border pl-4 gap-6;
}

.ci-section-title {
  @apply text-foreground text-sm font-semibold leading-5 h-7;
}

.ci-card-list {
  @apply w-full flex flex-col border border-solid border-border overflow-y-auto;
  border-radius: var(--radius);
}

.ci-card-item {
  @apply w-full px-4 py-3 bg-card border-b border-solid border-border flex items-center gap-3 cursor-pointer transition-colors;

  &:last-child {
    @apply border-b-0;
  }

  &:hover {
    @apply bg-muted;
  }

  &.is-active {
    @apply bg-muted;
  }

  &--skeleton {
    @apply cursor-default pointer-events-none;
  }
}

.ci-templates-head {
  @apply flex items-center gap-2;
}

.ci-filters {
  @apply w-full flex flex-col gap-2 flex-shrink-0;
}

.ci-filters-row {
  @apply w-full flex items-center gap-2;
}

.ci-filter-btn {
  @apply w-9 h-9 flex items-center justify-center rounded-md border border-solid border-border bg-card cursor-pointer text-muted-foreground p-0 flex-shrink-0;

  &:hover {
    @apply bg-muted text-foreground;
  }

  &.is-active {
    @apply bg-muted text-foreground;
  }
}

.ci-templates-body {
  @apply flex-1 overflow-y-auto w-full flex flex-col gap-6 pb-4;
}

.group {
  @apply w-full flex flex-col justify-start items-start gap-4;

  ul {
    @apply w-full border border-solid border-border flex flex-col justify-start items-start overflow-hidden;
    border-radius: var(--radius);

    li {
      @apply w-full px-4 py-3 bg-card border-b border-border flex flex-col justify-center items-start gap-4;

      &:last-child {
        @apply border-b-0;
      }

      &:hover {
        @apply bg-muted;
      }
    }
  }
}

/* Анимация появления правой панели */
.ci-slide-enter-active,
.ci-slide-leave-active {
  transition: opacity 0.3s ease 0.1s, transform 0.35s ease 0.1s;
}

.ci-slide-enter-from,
.ci-slide-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.ci-slide-enter-to,
.ci-slide-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
